<script setup lang="ts">
	import { useQueryClient } from '@tanstack/vue-query'
	import { computed, reactive, ref, watch } from 'vue'
	import { useRoute } from 'vue-router'
	import { useToast } from 'vue-toastification'

	import AlertComponent from '@/components/AlertComponent.vue'
	import LoadingModal from '@/components/LoadingModal.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledInput from '@/components/StyledInput.vue'
	import StyledSelect from '@/components/StyledSelect.vue'

	import useRatePlanCodes from '@/internal/modules/providers/composables/useRatePlanCodes'
	import { Hotel } from '@/internal/modules/providers/interfaces/Hotel'
	import { CreateRatePlanCodeParams } from '@/internal/modules/providers/interfaces/RatePlanCode'

	import useHotelRatePlanCodes from '../composables/useHotelRatePlanCodes'

	interface Props {
		hotel: Hotel
	}

	const toast = useToast()

	const router = useRoute()
	const props = defineProps<Props>()

	const queryClient = useQueryClient()

	// Cargar los códigos del hotel
	const {
		ratePlanCodes,
		isLoading,
		refetch,
		createRatePlanCodeAsync,
		createRatePlanCodeLoading,
	} = useHotelRatePlanCodes({
		immediate: true,
		params: {
			hotelId: router.params.id as string,
		},
		config: {
			errorHandling: true,
		},
	})

	const {
		deleteRatePlanCodeAsync,
		deleteRatePlanCodeLoading,

		updateRatePlanCodeAsync,
		updateRatePlanCodeLoading,

		restoreRatePlanCodeAsync,
		restoreRatePlanCodeLoading,
	} = useRatePlanCodes({
		immediate: false,
		config: {
			errorHandling: true,
		},
	})

	// Estado del formulario
	const form = reactive({
		code: '',
		type: 'agreement' as 'agreement' | 'public',
	})

	// Estado de alertas y modales
	const showDeleteModal = ref(false)
	const codeToDelete = ref<number | null>(null)

	// Estado de edición
	const isEditing = ref(false)
	const editingCodeId = ref<number | null>(null)

	// Estado de validación del código
	const codeError = ref('')
	const hasInteracted = ref(false) // Para saber si el usuario ha interactuado con el campo

	// Separar códigos por tipo y aplicación
	const hotelCodes = computed(() => {
		return ratePlanCodes.value || []
	})

	const hotelAgreementCodes = computed(() =>
		hotelCodes.value.filter((c) => c.type === 'agreement')
	)
	const hotelPublicCodes = computed(() =>
		hotelCodes.value.filter((c) => c.type === 'public')
	)

	// Validaciones de formato de código Rate Plan
	const validateRatePlanCode = (code: string): string => {
		if (!code.trim()) {
			return 'El código es requerido'
		}

		// El código debe tener exactamente 3 caracteres
		if (code.length !== 3) {
			return 'El código debe tener exactamente 3 caracteres'
		}

		// Solo letras y números permitidos (sin espacios ni caracteres especiales)
		const validFormat = /^[A-Z0-9]{3}$/
		if (!validFormat.test(code)) {
			return 'El código solo puede contener letras mayúsculas y números'
		}

		return ''
	}

	// Validar el código cuando cambia
	const handleCodeChange = (value: string) => {
		hasInteracted.value = true // Marcar que el usuario ha interactuado
		const upperValue = value.toUpperCase()
		form.code = upperValue
		codeError.value = validateRatePlanCode(upperValue)
	}

	// Error a mostrar solo después de interacción
	const displayError = computed(() => {
		return !hasInteracted.value ? codeError.value : ''
	})

	// Validaciones de límites
	const canAddAgreementCode = computed(() => {
		return hotelAgreementCodes.value.length < 1
	})

	const canAddPublicCode = computed(() => {
		return hotelPublicCodes.value.length < 3
	})

	const isCodeValid = computed(() => {
		return form.code.trim() && !codeError.value
	})

	const canSubmit = computed(() => {
		if (!isCodeValid.value) return false

		if (isEditing.value) return true

		// Si está creando, validar límites
		if (form.type === 'agreement') return canAddAgreementCode.value
		if (form.type === 'public') return canAddPublicCode.value
		return false
	})

	// Verificar si se puede mostrar el formulario (hay espacio disponible)
	const canShowForm = computed(() => {
		return canAddAgreementCode.value || canAddPublicCode.value
	})

	// Validar si el código ya existe en el hotel
	const checkCodeInHotel = (code: string): boolean => {
		return hotelCodes.value.some(
			(c) => c.code.toLowerCase() === code.toLowerCase()
		)
	}

	const startEdit = (code: any) => {
		isEditing.value = true
		editingCodeId.value = code.id
		originalType.value = code.type
		form.code = code.code
		form.type = code.type
		hasInteracted.value = true
		codeError.value = validateRatePlanCode(code.code)

		window.scrollTo({ top: 0, behavior: 'smooth' })
	}

	const originalType = ref<'agreement' | 'public' | null>(null)
	const cancelEdit = () => {
		isEditing.value = false
		editingCodeId.value = null
		originalType.value = null
		form.code = ''
		// Resetear al tipo disponible por defecto
		form.type = canAddAgreementCode.value ? 'agreement' : 'public'
		codeError.value = ''
		hasInteracted.value = false
	}

	const handleSubmit = async () => {
		if (!canSubmit.value) return

		// Si estamos editando
		if (isEditing.value && editingCodeId.value) {
			try {
				await updateRatePlanCodeAsync({
					id: editingCodeId.value,
					code: form.code.trim(),
					type: form.type,
				})

				cancelEdit()
				refetch()
			} catch (error) {
				console.error('Error al actualizar código rate plan:', error)
			}
			return
		}

		const codeExistsInHotel = checkCodeInHotel(form.code)

		if (codeExistsInHotel) {
			toast.error(
				`El código "${form.code}" ya está registrado en este hotel. No puedes agregar códigos duplicados.`
			)
			return
		}

		try {
			const params: CreateRatePlanCodeParams = {
				code: form.code.trim(),
				type: form.type,
				hotelId: props.hotel.id,
			}

			await createRatePlanCodeAsync(params)

			cancelEdit()
			refetch()
		} catch (error) {
			console.error('Error al crear código rate plan:', error)
		}
	}

	// Manejar eliminación
	const confirmDelete = (id: number) => {
		codeToDelete.value = id
		showDeleteModal.value = true
	}

	const handleDelete = async () => {
		if (codeToDelete.value === null) return

		try {
			await deleteRatePlanCodeAsync({ id: codeToDelete.value })
			showDeleteModal.value = false
			codeToDelete.value = null
			await refetch()
			queryClient.invalidateQueries(['rate-plan-codes'])
		} catch (error) {
			console.error('Error al eliminar código rate plan:', error)
		}
	}

	// Manejar restablecimiento
	const handleRestore = async (id: number) => {
		try {
			await restoreRatePlanCodeAsync({ id })
			await refetch()
			queryClient.invalidateQueries(['rate-plan-codes'])
			toast.success('Código restablecido exitosamente')
		} catch (error) {
			console.error('Error al restablecer código rate plan:', error)
		}
	}

	const typeOptions = [
		{ label: 'Convenio', value: 'agreement' },
		{ label: 'Tarifas públicas', value: 'public' },
	]

	// Opciones filtradas según disponibilidad
	const filteredTypeOptions = computed(() => {
		if (isEditing.value) {
			return typeOptions.filter((option) => {
				// Si está editando, mantener el tipo original siempre disponible
				if (option.value === originalType.value) return true

				// Para los otros tipos, verificar disponibilidad
				if (option.value === 'agreement') {
					return canAddAgreementCode.value
				}
				if (option.value === 'public') {
					return canAddPublicCode.value
				}
				return false
			})
		} else {
			return typeOptions.filter((option) => {
				if (option.value === 'agreement') {
					return canAddAgreementCode.value
				}
				if (option.value === 'public') {
					return canAddPublicCode.value
				}
				return true
			})
		}
	})

	// Auto-seleccionar cuando solo hay una opción disponible
	const autoSelectSingleOption = () => {
		if (!isEditing.value && filteredTypeOptions.value.length === 1) {
			form.type = filteredTypeOptions.value[0].value as 'agreement' | 'public'
		}
	}

	// Observar cambios en las opciones filtradas para auto-seleccionar
	watch(
		filteredTypeOptions,
		() => {
			autoSelectSingleOption()
		},
		{ immediate: true }
	)

	// Configuración de secciones de códigos del hotel
	const hotelCodeSections = computed(() => [
		{
			key: 'agreement',
			title: 'Convenio',
			codes: hotelAgreementCodes.value,
			icon: 'badge',
			iconColor: 'text-blue-600',
			bgGradient: 'from-blue-50 to-indigo-50',
			borderColor: 'border-blue-200',
			hoverBorder: 'hover:border-blue-300',
			iconBg: 'bg-blue-600',
			textColor: 'text-blue-900',
			showCount: false,
		},
		{
			key: 'public',
			title: 'Tarifas Públicas',
			codes: hotelPublicCodes.value,
			icon: 'public',
			iconColor: 'text-green-600',
			bgGradient: 'from-green-50 to-emerald-50',
			borderColor: 'border-green-200',
			hoverBorder: 'hover:border-green-300',
			iconBg: 'bg-green-600',
			textColor: 'text-green-900',
			showCount: true,
			count: `${hotelPublicCodes.value.length}/3`,
			countBg: 'bg-green-100',
			countText: 'text-green-700',
		},
	])
</script>

<template>
	<div class="bg-gradient-to-br from-gray-50 to-gray-100 h-full w-full p-6">
		<!-- Header Section -->
		<div class="mb-8">
			<div class="flex items-center gap-3 mb-3">
				<div
					class="bg-gradient-to-br from-blue-500 to-indigo-600 p-3 rounded-xl shadow-lg"
				>
					<span class="material-symbols-outlined text-white text-3xl"
						>confirmation_number</span
					>
				</div>
				<div>
					<h1 class="text-3xl font-bold text-gray-800">Rate Plan Codes</h1>
					<p class="text-gray-500 text-sm">
						Gestión de códigos para {{ props.hotel.name }}
					</p>
				</div>
			</div>

			<div class="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
				<p class="text-gray-600 text-sm mb-3 leading-relaxed">
					Gestiona los códigos Rate Plan Code específicos para este hotel. Si un
					código ya existe en la cadena, se aplicará automáticamente sin
					necesidad de registrarlo aquí.
				</p>
				<div
					class="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-4 rounded-lg"
				>
					<div class="flex items-start gap-3">
						<span class="material-symbols-outlined text-blue-600 mt-0.5"
							>info</span
						>
						<div>
							<p class="text-sm text-gray-700 font-medium mb-1">
								Formato válido
							</p>
							<p class="text-sm text-gray-600">
								3 caracteres alfanuméricos (letras mayúsculas y/o números)
							</p>
							<p class="text-xs text-gray-500 mt-2">
								<span class="font-semibold">Ejemplos:</span>
								<span class="inline-flex gap-2 ml-2">
									<span
										class="bg-white px-2 py-0.5 rounded font-mono text-blue-700"
										>COR</span
									>
									<span
										class="bg-white px-2 py-0.5 rounded font-mono text-blue-700"
										>PR1</span
									>
									<span
										class="bg-white px-2 py-0.5 rounded font-mono text-blue-700"
										>RAC</span
									>
									<span
										class="bg-white px-2 py-0.5 rounded font-mono text-blue-700"
										>A1B</span
									>
								</span>
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Alerta cuando se alcanza el máximo -->
		<div
			v-if="!canShowForm"
			class="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-6 rounded-xl shadow-md mb-8"
		>
			<div class="flex items-center gap-4">
				<div class="bg-green-500 p-3 rounded-full">
					<span class="material-symbols-outlined text-white text-2xl"
						>check_circle</span
					>
				</div>
				<div>
					<h3 class="font-bold text-gray-800 text-lg mb-1">
						Límite de códigos alcanzado
					</h3>
					<p class="text-gray-600 text-sm">
						Has registrado el máximo permitido de códigos Rate Plan para este
						hotel:
					</p>
					<ul class="mt-2 text-sm text-gray-700 space-y-1">
						<li class="flex items-center gap-2">
							<span class="material-symbols-outlined text-blue-600 text-sm"
								>check</span
							>
							<span
								><strong>Convenio:</strong>
								{{ hotelAgreementCodes.length }}/1</span
							>
						</li>
						<li class="flex items-center gap-2">
							<span class="material-symbols-outlined text-green-600 text-sm"
								>check</span
							>
							<span
								><strong>Tarifas Públicas:</strong>
								{{ hotelPublicCodes.length }}/3</span
							>
						</li>
					</ul>
				</div>
			</div>
		</div>

		<!-- Formulario de agregar/editar código -->
		<div
			v-if="canShowForm || isEditing"
			class="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-8"
		>
			<div class="flex items-center justify-between mb-6">
				<div class="flex items-center gap-2">
					<span
						class="material-symbols-outlined"
						:class="isEditing ? 'text-blue-600' : 'text-green-600'"
						>{{ isEditing ? 'edit' : 'add_circle' }}</span
					>
					<h2 class="text-xl font-bold text-gray-800">
						{{ isEditing ? 'Editar código' : 'Agregar nuevo código' }}
					</h2>
				</div>
				<button
					v-if="isEditing"
					@click="cancelEdit"
					class="text-gray-500 hover:text-gray-700 flex items-center gap-1 text-sm"
				>
					<span class="material-symbols-outlined text-sm">close</span>
					Cancelar
				</button>
			</div>

			<form
				@submit.prevent="handleSubmit"
				class="grid grid-cols-1 md:grid-cols-2 gap-6"
			>
				<StyledInput
					:model-value="form.code"
					@update:model-value="handleCodeChange"
					label="Código Rate Plan"
					placeholder="Ej: COR, PR1, RAC"
					:is-required="true"
					:disabled="createRatePlanCodeLoading"
					:max-length="3"
					:error="displayError"
					class="uppercase"
				>
					<template #labelCompnent>
						<span class="text-xs text-gray-500">(3 caracteres)</span>
					</template>
				</StyledInput>

				<StyledSelect
					v-model="form.type"
					label="Tipo de código"
					:options="filteredTypeOptions"
					:is-required="true"
					:disabled="
						createRatePlanCodeLoading ||
						updateRatePlanCodeLoading ||
						(isEditing && !canShowForm) ||
						filteredTypeOptions.length === 1
					"
					:placeholder="
						filteredTypeOptions.length === 0 ?
							'No hay opciones disponibles'
						:	'Selecciona un tipo'
					"
				>
					<template #labelCompnent>
						<span
							v-if="isEditing && !canShowForm"
							class="text-xs text-amber-600"
						>
							(no se puede cambiar - límites alcanzados)
						</span>
						<span
							v-else-if="!isEditing && filteredTypeOptions.length === 1"
							class="text-xs text-blue-600"
						>
							(seleccionado automáticamente)
						</span>
					</template>
				</StyledSelect>

				<div class="col-span-1 md:col-span-2 flex justify-end gap-3">
					<StyledButton
						v-if="isEditing"
						type="button"
						@click="cancelEdit"
						class="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-8 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2"
					>
						<span class="material-symbols-outlined text-xl">close</span>
						Cancelar
					</StyledButton>
					<StyledButton
						type="submit"
						:class="
							!canSubmit ? 'bg-gray-300 cursor-not-allowed'
							: isEditing ?
								'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'
							:	'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
						"
						class="text-white font-semibold px-8 py-3 rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-2"
						:disabled="
							!canSubmit ||
							createRatePlanCodeLoading ||
							updateRatePlanCodeLoading
						"
						:loading="createRatePlanCodeLoading || updateRatePlanCodeLoading"
					>
						<span class="material-symbols-outlined text-xl">{{
							isEditing ? 'save' : 'add'
						}}</span>
						{{ isEditing ? 'Guardar cambios' : 'Agregar código' }}
					</StyledButton>
				</div>
			</form>
		</div>

		<!-- Lista de códigos del hotel -->
		<div class="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-8">
			<div class="flex items-center gap-3 mb-6">
				<div
					class="bg-gradient-to-br from-blue-500 to-indigo-600 p-2 rounded-lg"
				>
					<span class="material-symbols-outlined text-white text-xl"
						>hotel</span
					>
				</div>
				<div>
					<h2 class="text-xl font-bold text-gray-800">Códigos de este hotel</h2>
					<p class="text-sm text-gray-500">{{ props.hotel.name }}</p>
				</div>
			</div>

			<div
				v-if="hotelCodes.length === 0"
				class="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300"
			>
				<span class="material-symbols-outlined text-6xl text-gray-400"
					>inventory_2</span
				>
				<p class="mt-3 text-gray-500 font-medium">
					No hay códigos registrados específicamente para este hotel
				</p>
			</div>

			<div
				v-else
				class="space-y-6"
			>
				<!-- Códigos del hotel - Iteración optimizada -->
				<div
					v-for="section in hotelCodeSections"
					:key="section.key"
					v-show="section.codes.length > 0"
				>
					<div class="flex items-center gap-2 mb-3">
						<span
							class="material-symbols-outlined text-xl"
							:class="section.iconColor"
							>{{ section.icon }}</span
						>
						<h3 class="font-bold text-gray-700 text-sm uppercase tracking-wide">
							{{ section.title }}
							<span
								v-if="section.showCount"
								class="px-2 py-0.5 rounded-full text-xs ml-2"
								:class="[section.countBg, section.countText]"
							>
								{{ section.count }}
							</span>
						</h3>
					</div>
					<div class="space-y-3">
						<div
							v-for="code in section.codes"
							:key="code.id"
							class="group flex items-center justify-between p-4 bg-gradient-to-r rounded-xl border hover:shadow-md transition-all duration-200"
							:class="[
								code.deletedAt ?
									'from-gray-50 to-gray-100'
								:	section.bgGradient,
								code.deletedAt ? 'border-gray-300' : section.borderColor,
								code.deletedAt ? 'hover:border-gray-400' : section.hoverBorder,
							]"
						>
							<div class="flex items-center gap-4 flex-1">
								<div
									class="p-2 rounded-lg shadow-sm"
									:class="code.deletedAt ? 'bg-gray-400' : section.iconBg"
								>
									<span class="material-symbols-outlined text-white text-xl">{{
										section.icon
									}}</span>
								</div>
								<div class="flex-1">
									<div class="flex items-center gap-2">
										<p
											class="font-bold text-lg font-mono"
											:class="
												code.deletedAt ?
													'text-gray-400 line-through'
												:	section.textColor
											"
										>
											{{ code.code }}
										</p>
										<span
											v-if="code.deletedAt"
											class="px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full"
										>
											Eliminado
										</span>
									</div>
									<p class="text-xs text-gray-600 flex items-center gap-1 mt-1">
										<span class="material-symbols-outlined text-xs"
											>calendar_today</span
										>
										<span class="font-medium">Creado:</span>
										{{
											new Date(code.createdAt).toLocaleDateString('es-ES', {
												year: 'numeric',
												month: 'short',
												day: 'numeric',
											})
										}}
									</p>
									<p
										v-if="code.deletedAt"
										class="text-xs text-red-600 flex items-center gap-1 mt-1"
									>
										<span class="material-symbols-outlined text-xs"
											>delete</span
										>
										<span class="font-medium">Eliminado:</span>
										{{
											new Date(code.deletedAt).toLocaleDateString('es-ES', {
												year: 'numeric',
												month: 'short',
												day: 'numeric',
											})
										}}
									</p>
								</div>
							</div>

							<!-- Botones normales cuando NO está eliminado -->
							<div
								v-if="!code.deletedAt"
								class="flex gap-2 transition-all duration-200"
							>
								<StyledButton
									type="button"
									class="bg-blue-500 hover:bg-blue-600 text-white text-sm py-2 px-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-1"
									@click="startEdit(code)"
									:disabled="
										deleteRatePlanCodeLoading || updateRatePlanCodeLoading
									"
								>
									<span class="material-symbols-outlined text-sm">edit</span>
									Editar
								</StyledButton>
								<StyledButton
									type="button"
									class="bg-red-500 hover:bg-red-600 text-white text-sm py-2 px-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-1"
									@click="confirmDelete(code.id)"
									:disabled="
										deleteRatePlanCodeLoading || updateRatePlanCodeLoading
									"
								>
									<span class="material-symbols-outlined text-sm">delete</span>
									Eliminar
								</StyledButton>
							</div>

							<!-- Botón de restablecer cuando SÍ está eliminado -->
							<div
								v-else
								class="flex gap-2 transition-all duration-200"
							>
								<StyledButton
									type="button"
									class="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-sm py-2 px-5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
									@click="handleRestore(code.id)"
									:disabled="restoreRatePlanCodeLoading"
									:loading="restoreRatePlanCodeLoading"
								>
									<span class="material-symbols-outlined text-sm">restore</span>
									Restablecer
								</StyledButton>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal de confirmación de eliminación -->
		<AlertComponent
			v-model="showDeleteModal"
			:dismiss="true"
			type="danger"
			@confirm="handleDelete"
			:loading="deleteRatePlanCodeLoading"
		>
			<template #title>Eliminar código rate plan</template>
			<template #body>
				¿Estás seguro de que deseas eliminar este código? Esta acción no se
				puede deshacer.
			</template>
			<template #closeButton>Cancelar</template>
			<template #button>Eliminar</template>
		</AlertComponent>

		<LoadingModal :when="isLoading" />
	</div>
</template>

<style scoped>
	.uppercase input {
		text-transform: uppercase;
	}
</style>
